import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
    MarketingPreferencesFields
} from '@interlayer/js/app/components/main/MarketingPreferences/components/MarketingPreferencesFields';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import Form from 'js/components/common/Form';

import { useGetErrorResponseMessage } from '@core/js/hooks/server';

import { CONTACT_TYPES_FRONT, SLOT_OPTIN_PRODUCT } from '@interlayer/js/app/components/main/MarketingPreferences/constants';
import { getContactPreferencesForSending } from '@interlayer/js/app/components/main/MarketingPreferences/helpers';
import { useInitialMarketingValues } from '@interlayer/js/app/components/main/MarketingPreferences/hooks/useInitialMarketingValues';
import {
    useShowOptedOutInformationPopup
} from '@interlayer/js/app/components/main/UserDetails/components/MarketingPreferences/hooks/useShowOptedOutInformationPopup';
import {
    useSlotClubOptedInPlayerTags
} from '@interlayer/js/app/components/main/UserDetails/components/MarketingPreferences/hooks/useSlotClubOptedInPlayerTags';
import { CONFIRMATION_STATUS_OF_TAG_REMOVAL } from '@interlayer/js/app/components/main/UserDetails/constants';
import { removePlayerTags } from '@interlayer/js/app/hooks/user/server';
import { removePlayerTag } from '@interlayer/js/hooks/user/store/buzz/actions';

import { changeUserContactPreferences } from 'js/app/components/popups/EmailChangePopup/components/server';
import { useTranslations } from 'js/hooks/i18n';
import { useFetchUserDetails } from 'js/hooks/user';
import { useShowNotification } from 'js/hooks/useShowNotification';

import style from './MarketingPreferences.scss';
import styleDetails from 'js/app/components/main/UserDetails/UserDetails.scss';

export const MarketingPreferences = ({
    tSuffix = '',
    isAdditionalContact = false,
    isAdditionalTextBlocks = false
}) => {
    const showNotification = useShowNotification();
    const fetchUserDetails = useFetchUserDetails();
    const t = useTranslations('playtech.mobile.system.userdetails.');
    const showOptedOutInformationPopup = useShowOptedOutInformationPopup();
    const initialValues = useInitialMarketingValues();
    const getErrorResponseMessage = useGetErrorResponseMessage();

    const slotClubOptedInPlayerTags = useSlotClubOptedInPlayerTags();
    const dispatch = useDispatch();
    const isSlotsClubPlayerTags = Boolean(slotClubOptedInPlayerTags.length);

    const initialValuesRef = useRef({
        ...initialValues,
        [CONTACT_TYPES_FRONT.slotClubParticipation]: isSlotsClubPlayerTags
    });

    const handlerSubmit = async (values, { restart }) => {
        const handlerAfterRemovalTag = () => changeUserContactPreferences(getContactPreferencesForSending(values))
            .then(fetchUserDetails)
            .then(() => {
                restart(values);
                showNotification(t('message.successUpdate'));
            })
            .catch(getErrorResponseMessage);

        if (
            isSlotsClubPlayerTags
            && !values[CONTACT_TYPES_FRONT.slotClubParticipation]
        ) {
            return removePlayerTags(slotClubOptedInPlayerTags)
                .then((response) => {
                    const removedPlayerTags = response?.data?.tags;

                    if (!removedPlayerTags) {
                        return Promise.reject(response);
                    }

                    let isForbiddenRemoveTag = false;

                    for (let index = 0; index < removedPlayerTags.length; index++) {
                        const playerTagData = removedPlayerTags[index];

                        if (playerTagData?.value !== CONFIRMATION_STATUS_OF_TAG_REMOVAL) {
                            isForbiddenRemoveTag = true;
                            continue;
                        }

                        dispatch(removePlayerTag(playerTagData.name));
                    }

                    if (isForbiddenRemoveTag) {
                        return Promise.reject(response);
                    }
                })
                .catch(() => Promise.reject({
                    data: {
                        errorText: t('error.optedOut')
                    }
                }))
                .then(handlerAfterRemovalTag)
                .catch(getErrorResponseMessage);
        }

        return handlerAfterRemovalTag();
    };

    const onChange = (event, form) => {
        const { name } = event.target;
        const { values } = form.getState();

        if (
            [
                `${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.email}`,
                `${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.SMS}`
            ].includes(name)
            && !event.target.checked
            && isSlotsClubPlayerTags
            && values[CONTACT_TYPES_FRONT.slotClubParticipation]
        ) {
            const isSlotClubOptOut =
                !values[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.email}`]
                && !values[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.SMS}`];

            form.change(name, true);
            showOptedOutInformationPopup({
                type: isSlotClubOptOut ? 'slotClubOptOut' : name,
                changeValue: () => {
                    form.change(name, false);

                    if (isSlotClubOptOut) {
                        form.change(CONTACT_TYPES_FRONT.slotClubParticipation, false);
                    }
                }
            });

            return;
        }

        if (
            name === CONTACT_TYPES_FRONT.slotClubParticipation
            && event.target.checked
            && (
                !values[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.email}`]
                || !values[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.SMS}`]
            )
        ) {
            form.change(
                `${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.SMS}`,
                initialValuesRef.current[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.SMS}`]
            );
            form.change(
                `${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.email}`,
                initialValuesRef.current[`${SLOT_OPTIN_PRODUCT}_${CONTACT_TYPES_FRONT.email}`]
            );
        }
    };

    return (
        <Form
            onSubmit={handlerSubmit}
            initialValues={initialValuesRef.current}
            render={({ submitting, submitError, handleSubmit, pristine }) => (
                <form className={style.container} onSubmit={handleSubmit}>
                    {submitError && (
                        <ErrorMessage
                            className={styleDetails.error}
                            error={submitError}
                            animate
                            isFormError
                        />
                    )}

                    <MarketingPreferencesFields
                        isAdditionalContact={isAdditionalContact}
                        isAdditionalTextBlocks={isAdditionalTextBlocks}
                        isSlotsClubPlayerTags={isSlotsClubPlayerTags}
                        onChange={onChange}
                        tSuffix={tSuffix}
                    />

                    <Button
                        type="submit"
                        className={styleDetails.button}
                        disabled={submitting || pristine}
                    >
                        {t('marketingPreferences.button')}
                    </Button>
                </form>
            )}
        />
    );
};
